import React, { useState } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { FormattedMessage } from '../../util/reactIntl';
import { logout } from '../../ducks/auth.duck';
import { landingPageLinkTo } from '../../util/publicHome';

import { InlineTextButton } from '../Button/Button';
import NamedLink from '../NamedLink/NamedLink';
import SiteDrawer from '../SiteDrawer/SiteDrawer';

import css from './GuestMarketingTopbar.module.css';

const MARK = '/static/branding/sweet-listeners-ear-heart.png';

/**
 * Underline How it Works on that page; hash links only when the hash matches.
 *
 * @param {{ name: string, to?: { hash?: string } }} link
 * @param {string} [currentPage]
 * @returns {boolean}
 */
export const guestNavLinkIsCurrent = (link, currentPage) => {
  if (link.to?.hash) {
    if (typeof window === 'undefined') {
      return false;
    }
    return currentPage === link.name && window.location.hash === `#${link.to.hash}`;
  }
  return currentPage === link.name;
};

/**
 * Compact header mark. HTML width/height stay small so the 558×448 PNG
 * cannot blow the 390 bar or cover the FAQ title.
 *
 * @param {Object} props
 * @param {boolean} [props.isLogin]
 * @returns {JSX.Element}
 */
const HeaderBrand = ({ isLogin }) => (
  <span className={classNames(css.lockup, isLogin ? css.lockupLogin : null)}>
    <img
      className={css.markImg}
      src={MARK}
      alt=""
      width={isLogin ? 48 : 32}
      height={isLogin ? 38 : 26}
    />
    <span className={css.word}>
      <span className={css.wordmark}>
        <span className={css.sweet}>Sweet</span> <span className={css.listeners}>Listeners</span>
      </span>
      {isLogin ? <span className={css.tagline}>listen · care · connect</span> : null}
    </span>
  </span>
);

/**
 * Slim marketing header. Guests see hamburger + brand + Log In / Sign Up.
 * How it Works, About, and FAQ live in the side drawer (GUEST_HEADER_NAV).
 * If a session is already open, show Dashboard + Log out so the chrome
 * is never a dead end.
 *
 * @param {Object} props
 * @param {string} [props.currentPage]
 * @returns {JSX.Element}
 */
const GuestMarketingTopbar = props => {
  const { currentPage } = props;
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(state => !!state.auth?.isAuthenticated);
  const currentUser = useSelector(state => state.user?.currentUser);
  const currentUserHasListings = useSelector(state => !!state.user?.currentUserHasListings);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);
  const handleLogout = () => {
    closeMenu();
    Promise.resolve(dispatch(logout())).then(() => {
      if (typeof window !== 'undefined') window.location = '/login';
    });
  };
  const isLogin = currentPage === 'LoginPage';

  return (
    <div
      className={classNames(css.wrap, isLogin ? css.wrapLogin : null)}
      data-guest-topbar="marketing"
      data-home-header={currentPage === 'LandingPage' ? 'guest' : undefined}
    >
      <header className={css.header}>
        <button
          type="button"
          className={css.menu}
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
          aria-expanded={menuOpen}
        >
          <span className={css.menuGlyph} aria-hidden="true">
            ☰
          </span>
        </button>
        <NamedLink
          name="LandingPage"
          to={landingPageLinkTo({ isAuthenticated })}
          className={classNames(css.brand, isLogin ? css.brandLogin : null)}
          aria-label="Sweet Listeners"
        >
          <HeaderBrand isLogin={isLogin} />
        </NamedLink>
        {isAuthenticated ? (
          <div className={css.auth} data-guest-auth="account">
            <NamedLink name="DashboardPage" className={css.loginPill}>
              <FormattedMessage id="TopbarDesktop.dashboard" />
            </NamedLink>
            <InlineTextButton
              rootClassName={css.logoutBtn}
              onClick={handleLogout}
              data-signed-in-logout="true"
            >
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </div>
        ) : (
          <div className={css.auth} data-guest-auth="pills">
            <NamedLink
              name="LoginPage"
              className={classNames(
                css.loginPill,
                currentPage === 'LoginPage' ? css.loginOn : null
              )}
            >
              <LoginIcon />
              <FormattedMessage id="GuestTopbar.login" />
            </NamedLink>
            <NamedLink name="SignupPage" className={css.signupBtn}>
              <SignupIcon />
              <FormattedMessage id="GuestTopbar.signup" />
            </NamedLink>
          </div>
        )}
      </header>
      {menuOpen ? (
        <>
          <div className={css.overlay} onClick={closeMenu} role="presentation" />
          <aside className={css.drawer} aria-label="Site">
            <SiteDrawer
              isAuthenticated={isAuthenticated}
              currentPage={currentPage}
              currentUser={currentUser}
              currentUserHasListings={currentUserHasListings}
              onLogout={handleLogout}
              onClose={closeMenu}
            />
          </aside>
        </>
      ) : null}
    </div>
  );
};

const LoginIcon = () => (
  <span className={css.authIcon} aria-hidden="true">
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="5.5" r="2.4" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M3.2 13c.7-2.3 2.5-3.5 4.8-3.5s4.1 1.2 4.8 3.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  </span>
);

const SignupIcon = () => (
  <span className={css.authIcon} aria-hidden="true">
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 3.2v9.6M3.2 8h9.6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  </span>
);

GuestMarketingTopbar.defaultProps = {
  currentPage: null,
};

GuestMarketingTopbar.propTypes = {
  currentPage: string,
};

export default GuestMarketingTopbar;
